// UniFi Cloud API Service - Integration with UniFi Cloud using API keys
class UniFiCloudAPIService {
  constructor() {
    this.config = null;
    this.apiKey = null;
    this.baseURL = 'https://unifi.ui.com/api';
    this.site = 'default';
  }

  // Initialize the service with configuration
  initialize(config) {
    this.config = config;
    this.apiKey = config.apiKey;
    this.site = config.site || 'default';
  }

  // Load configuration from localStorage
  loadConfig() {
    try {
      const savedConfig = localStorage.getItem('unifiCloudConfig');
      if (savedConfig) {
        this.config = JSON.parse(savedConfig);
        this.apiKey = this.config.apiKey;
        this.site = this.config.site || 'default';
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading UniFi Cloud config:', error);
      return false;
    }
  }

  // Save configuration to localStorage
  saveConfig(config) {
    try {
      const configToSave = {
        ...config,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('unifiCloudConfig', JSON.stringify(configToSave));
      this.initialize(configToSave);
      return true;
    } catch (error) {
      console.error('Error saving UniFi Cloud config:', error);
      return false;
    }
  }

  // Generic API request method
  async makeRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      console.log(`UniFi Cloud API Request: ${url}`);
      
      const defaultHeaders = {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      });

      console.log(`UniFi Cloud API Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`UniFi Cloud API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('UniFi Cloud API Request Error:', error);
      return { success: false, error: error.message };
    }
  }

  // Simple connectivity test
  async testConnectivity() {
    try {
      console.log('Testing basic connectivity to UniFi Cloud...');
      
      // Test basic connectivity first
      const testUrl = 'https://unifi.ui.com';
      const response = await fetch(testUrl, { method: 'HEAD' });
      console.log('Basic connectivity test:', response.status);
      
      if (response.ok) {
        console.log('✅ Basic connectivity to UniFi Cloud successful');
        return { success: true, message: 'Basic connectivity OK' };
      } else {
        console.log('❌ Basic connectivity failed:', response.status);
        return { success: false, error: `Basic connectivity failed: ${response.status}` };
      }
    } catch (error) {
      console.error('❌ Connectivity test error:', error);
      return { success: false, error: error.message };
    }
  }

  // Authentication test
  async testConnection() {
    try {
      console.log('Testing UniFi Cloud connection...');
      console.log('Base URL:', this.baseURL);
      console.log('API Key:', this.apiKey ? '***' + this.apiKey.slice(-4) : 'Not set');
      
      // Try the correct UniFi Cloud API endpoint
      const result = await this.makeRequest('/v1/self');
      
      if (result.success) {
        console.log('UniFi Cloud connection successful:', result.data);
      } else {
        // Try alternative endpoint
        console.log('Trying alternative endpoint...');
        const altResult = await this.makeRequest('/v1/self/sites');
        if (altResult.success) {
          console.log('UniFi Cloud connection successful (alternative):', altResult.data);
          return altResult;
        } else {
          console.error('UniFi Cloud connection failed:', result.error);
        }
      }
      
      return result;
    } catch (error) {
      console.error('UniFi Cloud test connection error:', error);
      return { success: false, error: error.message };
    }
  }

  // Sites Management
  async getSites() {
    try {
      const result = await this.makeRequest('/v1/self/sites');
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Network Devices
  async getDevices(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/devices`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getClients(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/clients`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getNetworkStats(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/dashboard`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Access Control
  async getAccessDevices(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/access/devices`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAccessUsers(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/access/users`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAccessLogs(site = null, limit = 100) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/access/logs?limit=${limit}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async unlockDoor(deviceId, site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/access/devices/${deviceId}/unlock`, {
        method: 'POST',
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async lockDoor(deviceId, site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/access/devices/${deviceId}/lock`, {
        method: 'POST',
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Protect Cameras
  async getProtectCameras(site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/protect/cameras`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getProtectRecordings(cameraId, startTime, endTime, site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(
        `/v1/sites/${targetSite}/protect/cameras/${cameraId}/recordings?start=${startTime}&end=${endTime}`
      );
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getCameraSnapshot(cameraId, site = null) {
    try {
      const targetSite = site || this.site;
      const result = await this.makeRequest(`/v1/sites/${targetSite}/protect/cameras/${cameraId}/snapshot`);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // System Status
  async getSystemStatus() {
    try {
      const result = await this.makeRequest('/v1/self/status');
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Device Discovery
  async discoverDevices() {
    try {
      const [devicesResult, accessResult, protectResult] = await Promise.all([
        this.getDevices(),
        this.getAccessDevices(),
        this.getProtectCameras(),
      ]);

      return {
        success: true,
        data: {
          network: devicesResult.success ? devicesResult.data : [],
          access: accessResult.success ? accessResult.data : [],
          protect: protectResult.success ? protectResult.data : [],
        },
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Utility methods
  isConfigured() {
    return !!(this.config && this.apiKey);
  }

  isConnected() {
    return !!(this.apiKey);
  }

  getConfig() {
    return this.config;
  }

  handleError(error) {
    console.error('UniFi Cloud API Error:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred',
    };
  }
}

export default UniFiCloudAPIService; 